import { ArrowRight, Code } from "lucide-react";
import type { ReactNode } from "react";

const DEFAULT_FEATURES = [
  "Secure API key management",
  "Webhook configuration",
  "Usage analytics & billing",
];

/**
 * Left-hand branding panel shared by full-page onboarding views. It is hidden
 * below the `lg` breakpoint and occupies half the shell width when visible.
 *
 * @param description Explanatory copy rendered beneath the dashboard heading.
 * @param featuresHeading Heading rendered above the feature list.
 * @param features Optional feature labels; defaults to the API Dashboard list.
 * @returns The responsive branding sidebar.
 */
export const BrandingSidebar = ({
  description,
  featuresHeading,
  features = DEFAULT_FEATURES,
}: {
  description: string;
  featuresHeading: string;
  features?: string[];
}) => {
  return (
    <div className="hidden flex-col justify-center items-center p-12 lg:flex lg:w-1/2 bg-primary/10">
      <div className="max-w-xl">
        <Code size={64} className="mb-8 text-primary" />
        <h1 className="mb-4 text-4xl font-bold">Sully.ai API Dashboard</h1>
        <p className="mb-6 text-xl text-muted-foreground">{description}</p>

        <div className="mt-8 space-y-4">
          <h3 className="text-xl font-semibold">{featuresHeading}</h3>
          <ul className="space-y-3">
            {features.map((feature) => (
              <li key={feature} className="flex items-center">
                <div className="p-1 mr-3 rounded-full bg-primary/20">
                  <ArrowRight size={16} className="text-primary" />
                </div>
                <span>{feature}</span>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </div>
  );
};

/**
 * Full-page onboarding shell with an optional branding sidebar and a centered
 * content card. The shell can grow beyond the viewport and keeps tall steps scrollable.
 *
 * @param sidebar Optional branding content rendered in the shell's left region.
 * When omitted, the content column is centered across the full viewport.
 * @param children Primary onboarding-step content rendered in the right column.
 * @param footer Optional content rendered beneath the primary content column.
 * @returns The responsive, vertically scrollable onboarding shell.
 */
export const OnboardingLayout = ({
  sidebar,
  children,
  footer,
}: {
  sidebar?: ReactNode;
  children: ReactNode;
  footer?: ReactNode;
}) => {
  return (
    <div className="flex min-h-screen w-full bg-secondary">
      {sidebar}

      <div
        className={`relative flex flex-col justify-center items-center overflow-y-auto px-5 py-10 w-full md:p-12 ${sidebar ? "lg:w-1/2" : ""}`}
      >
        <div
          className={`relative z-10 w-full space-y-8 rounded-xl bg-background p-6 sm:p-11 ${sidebar ? "max-w-md" : "max-w-[580px]"}`}
        >
          {children}
        </div>
        {footer && <div className="relative z-10">{footer}</div>}
      </div>
    </div>
  );
};
